/**
 * /admin/tier-settings — super_admin panel (inherits the /admin gate).
 *
 * Two sections:
 *  1. Chat Labels — set a tier's chat display alias (reuses the conversations.title
 *     rename lever via the super_admin set_tier_chat_title RPC). Identity (code /
 *     tier name) is never touched. Empty resets to the tier name.
 *  2. Chat Admins — tier-scoped. Chat is one conversation per tier, so members are
 *     managed per tier (not per feed-channel). Drives the mig-367 RPCs (fixed in 375).
 */

import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Tag, Users, Trash2, Search, Image as ImageIcon, Upload, RotateCcw } from 'lucide-react';
import {
  useTierChatLabels,
  useSetTierChatTitle,
  useTierChannels,
  useChannelMembers,
  useUpdateChannelMemberRole,
  useRemoveChannelMember,
  useAddChannelMembers,
  useAddableCommunityUsers,
  useTierIdentities,
  useSetTierIdentity,
} from '@/api/community-hooks';
import { uploadTierBadge, type TierChatLabel, type TierIdentityRow } from '@/api/community';
import { getAllTierConfigs, type TierConfigEntry } from '@/lib/tier-config';
import Avatar from '@/components/community/Avatar';

export const Route = createFileRoute('/admin/tier-settings')({
  component: TierSettingsPage,
});

type Section = 'labels' | 'admins' | 'identity';

function TierSettingsPage() {
  const [section, setSection] = useState<Section>('labels');

  return (
    <div className="flex h-full min-h-0">
      {/* Section nav */}
      <div className="w-56 flex-shrink-0 border-r border-border p-3 space-y-1">
        <h1 className="px-2 pb-2 text-sm font-semibold text-foreground">Tier Settings</h1>
        <SectionButton active={section === 'labels'} onClick={() => setSection('labels')} icon={<Tag className="h-4 w-4" />} label="Chat Labels" />
        <SectionButton active={section === 'admins'} onClick={() => setSection('admins')} icon={<Users className="h-4 w-4" />} label="Chat Admins" />
        <SectionButton active={section === 'identity'} onClick={() => setSection('identity')} icon={<ImageIcon className="h-4 w-4" />} label="Identity" />
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1 overflow-y-auto p-6">
        {section === 'labels' ? <ChatLabelsSection /> : section === 'admins' ? <ChatAdminsSection /> : <IdentitySection />}
      </div>
    </div>
  );
}

function SectionButton({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-2 rounded-lg px-2 py-2 text-sm transition-colors ${
        active ? 'bg-primary/15 text-primary font-medium' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

// ─── Section 1: Chat Labels ─────────────────────────────────────────────────

function ChatLabelsSection() {
  const { data: tiers, isLoading, isError } = useTierChatLabels();

  if (isLoading) return <Centered><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></Centered>;
  if (isError || !tiers) return <p className="text-sm text-danger">Failed to load tiers.</p>;

  return (
    <div className="max-w-2xl space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Chat Labels</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Rename how a tier's community chat is displayed. This is a display alias only — the tier
          identity, pricing, badges and access are unchanged. Clear the field to reset to the tier name.
        </p>
      </div>
      <div className="divide-y divide-border rounded-xl border border-border">
        {tiers.map((t) => (
          <TierLabelRow key={t.tier_id} tier={t} allTiers={tiers} />
        ))}
      </div>
    </div>
  );
}

function TierLabelRow({ tier, allTiers }: { tier: TierChatLabel; allTiers: TierChatLabel[] }) {
  const [value, setValue] = useState(tier.is_aliased ? (tier.title ?? '') : '');
  const setTitle = useSetTierChatTitle();
  const hasChat = tier.conversation_id !== null;

  const effective = value.trim() === '' ? tier.tier_name : value.trim();
  const dirty = effective !== (tier.title ?? tier.tier_name);

  // Soft, non-blocking collision warning: another tier already shows this label.
  const collision = useMemo(() => {
    const label = effective.toLowerCase();
    return allTiers.find(
      (o) => o.tier_id !== tier.tier_id && (o.title ?? o.tier_name).toLowerCase() === label,
    );
  }, [effective, allTiers, tier.tier_id]);

  const save = () => {
    setTitle.mutate(
      { tierId: tier.tier_id, title: value.trim() === '' ? null : value.trim() },
      {
        onSuccess: (r) => toast.success(r.is_aliased ? `Now shown as “${r.title}”` : `Reset to “${r.tier_name}”`),
        onError: (e) => toast.error(e instanceof Error ? e.message : 'Save failed'),
      },
    );
  };

  return (
    <div className="flex items-start gap-4 p-4">
      <div className="w-32 flex-shrink-0 pt-2">
        <p className="text-sm font-medium text-foreground">{tier.tier_name}</p>
        <p className="font-mono text-[11px] text-muted-foreground">{tier.code}</p>
      </div>
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex items-center gap-2">
          <input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={hasChat ? tier.tier_name : 'No chat conversation for this tier'}
            disabled={!hasChat || setTitle.isPending}
            className="min-w-0 flex-1 rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground disabled:opacity-50 focus:border-primary focus:outline-none"
          />
          <button
            type="button"
            onClick={save}
            disabled={!hasChat || !dirty || setTitle.isPending}
            className="rounded-lg bg-primary/15 px-3 py-2 text-sm font-medium text-primary transition-colors hover:bg-primary/25 disabled:opacity-40"
          >
            {setTitle.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
          </button>
        </div>
        {hasChat && (
          <p className="text-xs text-muted-foreground">
            Shows as <span className="font-medium text-foreground">{effective}</span>
          </p>
        )}
        {collision && (
          <p className="text-xs text-warning">
            Heads up: “{effective}” is also used by <span className="font-medium">{collision.tier_name}</span> — members may find it ambiguous.
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Section 2: Chat Admins (tier-scoped) ───────────────────────────────────

function ChatAdminsSection() {
  const { data: tiers } = useTierChatLabels();
  const chatTiers = useMemo(() => (tiers ?? []).filter((t) => t.conversation_id !== null), [tiers]);
  const [tierId, setTierId] = useState<string>('');

  // All feed channels of a tier resolve to the same chat conversation, so any
  // channel id drives the (fixed) member RPCs. Use the tier's first channel.
  const { data: channels } = useTierChannels(tierId || null);
  const channelId = channels?.[0]?.id;

  return (
    <div className="max-w-2xl space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Chat Admins</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Manage members and admins of a tier's community chat. Chat is one conversation per tier,
          so this is scoped to the whole tier chat (not individual feed channels).
        </p>
      </div>

      <select
        value={tierId}
        onChange={(e) => setTierId(e.target.value)}
        className="rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none"
      >
        <option value="">Select a tier…</option>
        {chatTiers.map((t) => (
          <option key={t.tier_id} value={t.tier_id}>{t.tier_name}</option>
        ))}
      </select>

      {tierId && !channelId && <p className="text-sm text-muted-foreground">This tier has no channels.</p>}
      {channelId && <ChatMembersManager channelId={channelId} />}
    </div>
  );
}

function ChatMembersManager({ channelId }: { channelId: string }) {
  const { data: members, isLoading } = useChannelMembers(channelId);
  const updateRole = useUpdateChannelMemberRole();
  const removeMember = useRemoveChannelMember();

  if (isLoading) return <Centered><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></Centered>;

  const setRole = (userId: string, role: 'admin' | 'member') =>
    updateRole.mutate(
      { channelId, userId, role },
      { onError: (e) => toast.error(e instanceof Error ? e.message : 'Update failed') },
    );

  const remove = (userId: string, name: string) =>
    removeMember.mutate(
      { channelId, userId, archivePosts: false },
      {
        onSuccess: () => toast.success(`Removed ${name}`),
        onError: (e) => toast.error(e instanceof Error ? e.message : 'Remove failed'),
      },
    );

  return (
    <div className="space-y-4">
      <AddMemberBox channelId={channelId} />
      <div className="divide-y divide-border rounded-xl border border-border">
        {(members ?? []).map((m) => (
          <div key={m.user_id} className="flex items-center gap-3 p-3">
            <Avatar src={m.avatar_url} name={m.full_name} size="sm" className="flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-foreground">{m.full_name}</p>
              <p className="text-xs text-muted-foreground capitalize">
                {m.role}{m.is_super_admin ? ' · super admin' : ''}
              </p>
            </div>
            {m.role === 'owner' ? (
              <span className="text-xs text-muted-foreground">owner</span>
            ) : (
              <div className="flex items-center gap-1">
                {m.role === 'admin' ? (
                  <button type="button" onClick={() => setRole(m.user_id, 'member')} className="rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground">Demote</button>
                ) : (
                  <button type="button" onClick={() => setRole(m.user_id, 'admin')} className="rounded-md bg-primary/15 px-2 py-1 text-xs font-medium text-primary hover:bg-primary/25">Make admin</button>
                )}
                <button type="button" onClick={() => remove(m.user_id, m.full_name)} className="rounded-md p-1.5 text-muted-foreground hover:bg-danger/10 hover:text-danger" aria-label={`Remove ${m.full_name}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function AddMemberBox({ channelId }: { channelId: string }) {
  const [query, setQuery] = useState('');
  const { data: results } = useAddableCommunityUsers(channelId, query);
  const add = useAddChannelMembers();

  const addUser = (userId: string, name: string) => {
    add.mutate(
      { channelId, userIds: [userId] },
      {
        onSuccess: () => { toast.success(`Added ${name}`); setQuery(''); },
        onError: (e) => toast.error(e instanceof Error ? e.message : 'Add failed'),
      },
    );
  };

  return (
    <div className="relative">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Add a member by name…"
        className="w-full rounded-lg border border-border bg-card py-2 pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
      />
      {query.length >= 1 && results && results.length > 0 && (
        <div className="absolute z-10 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-border bg-popover shadow-lg">
          {results.map((u: { id?: string; user_id?: string; full_name?: string | null; name?: string | null; avatar_url?: string | null }) => {
            const id = (u.id ?? u.user_id) as string;
            const name = u.full_name ?? u.name ?? 'Unknown';
            return (
              <button key={id} type="button" onClick={() => addUser(id, name)} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted">
                <Avatar src={u.avatar_url ?? null} name={name} size="xs" className="flex-shrink-0" />
                <span className="truncate text-foreground">{name}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Section 3: Identity (data-driven tier badges) ──────────────────────────

function IdentitySection() {
  const { data: rows, isLoading, isError } = useTierIdentities();
  const tiers = useMemo(() => getAllTierConfigs().filter((t) => t.code !== 'ADMIN'), []);
  const rowByCode = useMemo(() => {
    const m = new Map<string, TierIdentityRow>();
    for (const r of rows ?? []) m.set(r.code.toUpperCase(), r);
    return m;
  }, [rows]);

  if (isLoading) return <Centered><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></Centered>;
  if (isError) return <p className="text-sm text-danger">Failed to load tiers.</p>;

  return (
    <div className="max-w-3xl space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Identity</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Each tier's badge and accent colour. These render across the community hub, chat, stories,
          events and tier badges — changes apply everywhere without a deploy. Badges are square
          (SVG recommended); reset restores the shipped default.
        </p>
      </div>
      <div className="divide-y divide-border rounded-xl border border-border">
        {tiers.map((t) => (
          <TierIdentityRowEditor key={t.code} config={t} dbRow={rowByCode.get(t.code) ?? null} />
        ))}
      </div>
    </div>
  );
}

function TierIdentityRowEditor({ config, dbRow }: { config: TierConfigEntry; dbRow: TierIdentityRow | null }) {
  const setIdentity = useSetTierIdentity();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  // Effective current values: DB override, else the shipped default from config.
  const effectiveIcon = dbRow?.icon_url ?? config.badgeSrc ?? null;
  const effectiveAccent = dbRow?.accent_color ?? config.accentColor;

  const [accent, setAccent] = useState(effectiveAccent);
  useEffect(() => { setAccent(effectiveAccent); }, [effectiveAccent]);

  const accentValid = /^#[0-9a-fA-F]{6}$/.test(accent);
  const busy = setIdentity.isPending || uploading;

  // A tier is "customised" once its DB values diverge from the shipped defaults.
  const isCustomised =
    (!!dbRow?.icon_url && dbRow.icon_url !== config.badgeSrc) ||
    (!!dbRow?.accent_color && dbRow.accent_color !== config.accentColor);

  // admin_set_tier_identity writes BOTH columns every call — always pass both.
  const save = (iconUrl: string | null, accentColor: string | null, msg: string) =>
    setIdentity.mutate(
      { code: config.code, iconUrl, accentColor },
      {
        onSuccess: () => toast.success(msg),
        onError: (e) => toast.error(e instanceof Error ? e.message : 'Save failed'),
      },
    );

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    if (file.size > 2_097_152) { toast.error('Badge must be under 2 MB'); return; }
    try {
      setUploading(true);
      const url = await uploadTierBadge(config.code, file);
      save(url, accent, `${config.label} badge updated`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex items-start gap-4 p-4">
      {/* Preview */}
      <div className="flex w-16 flex-shrink-0 flex-col items-center gap-1.5">
        <div className="h-14 w-14 overflow-hidden rounded-xl ring-1 ring-border">
          {effectiveIcon ? (
            <img src={effectiveIcon} alt="" className="h-full w-full object-cover" draggable={false} />
          ) : (
            <div className="grid h-full w-full place-items-center text-xl">{config.emoji}</div>
          )}
        </div>
        <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
          <span
            className="h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: accentValid ? accent : effectiveAccent }}
          />
          chip
        </span>
      </div>

      {/* Controls */}
      <div className="min-w-0 flex-1 space-y-3">
        <div>
          <p className="text-sm font-medium text-foreground">{config.label}</p>
          <p className="font-mono text-[11px] text-muted-foreground">{config.code}</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary/15 px-3 py-2 text-sm font-medium text-primary transition-colors hover:bg-primary/25 disabled:opacity-40"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            Upload badge
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/svg+xml,image/png,image/webp,image/jpeg"
            className="hidden"
            onChange={onFile}
          />

          {/* Accent colour */}
          <label className="inline-flex items-center gap-2 rounded-lg border border-border px-2 py-1.5">
            <input
              type="color"
              value={accentValid ? accent : '#000000'}
              onChange={(e) => setAccent(e.target.value)}
              className="h-6 w-6 cursor-pointer rounded border-0 bg-transparent p-0"
              aria-label={`${config.label} accent colour`}
            />
            <input
              value={accent}
              onChange={(e) => setAccent(e.target.value)}
              spellCheck={false}
              className="w-20 bg-transparent font-mono text-xs text-foreground focus:outline-none"
            />
          </label>
          <button
            type="button"
            onClick={() => save(effectiveIcon, accent, `${config.label} accent updated`)}
            disabled={busy || !accentValid || accent === effectiveAccent}
            className="rounded-lg bg-primary/15 px-3 py-2 text-sm font-medium text-primary transition-colors hover:bg-primary/25 disabled:opacity-40"
          >
            Save accent
          </button>

          {isCustomised && (
            <button
              type="button"
              onClick={() => save(null, null, `${config.label} reset to default`)}
              disabled={busy}
              className="ml-auto inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-40"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Reset
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return <div className="flex items-center justify-center py-10">{children}</div>;
}
